import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SwalService } from 'src/app/services/alert/swal.service';
import { EventClosingService, ClosingStatus, CashierTotals } from 'src/app/services/event-closing/event-closing.service';
import { ConnectivityService } from 'src/app/services/connectivity/connectivity.service';
import {
    CashCount, CashCountLine, CashCountTotals, CurrencyProfile,
    cashCountForSave, cashCountTotals, isCounted, lineSubtotal, newCashCount, restoreCashCount,
} from 'src/app/shared/cash-count/cash-count';

/**
 * Cierre de evento. Tiene dos modos según configurations.event_closing_active:
 *
 *  - Cierre exigido: se cuenta el efectivo billete por billete y moneda por
 *    moneda, en la moneda del país, y el sistema calcula la diferencia
 *    contra lo esperado (fondo + ventas en efectivo − gastos).
 *  - Cierre no exigido: solo se pide un visto bueno sobre el reporte, que
 *    deja constancia de quién revisó y cuándo.
 */
@Component({
    selector: 'app-event-closing',
    templateUrl: './event-closing.component.html',
    styleUrls: ['./event-closing.component.scss'],
})
export class EventClosingComponent implements OnInit {

    @Input() event_key: string = '';
    @Input() event_name: string = '';

    /** Caja que cierra: la zona del evento (p. ej. "Barra 1"). La pasa quien abre el modal. */
    @Input() box_key: string = '';
    @Input() box_name: string = '';

    /** El rol 11 abre el cierre de otra caja solo para consultarlo. */
    @Input() readonly: boolean = false;

    /**
     * Cuenta del cajero/estación. Si viene, el cierre suma solo las órdenes
     * que vendió ese usuario en la caja; vacío, todas las de la caja (audit).
     */
    @Input() account_key: string = '';

    /**
     * Totales del sistema. Los que pasa la página son solo el arranque: al
     * abrir, el modal trae todas las órdenes de la caja y los recalcula.
     */
    @Input() total_orders: number = 0;
    @Input() total_sales: number = 0;
    @Input() total_cost_admin: number = 0;
    @Input() total_cash: number = 0;
    @Input() total_card: number = 0;
    @Input() total_pos: number = 0;

    closingRequired: boolean = false;
    loading: boolean = true;
    saving: boolean = false;
    /** Las órdenes de la caja no se pudieron leer: se cierra con lo que había en pantalla. */
    orders_error: boolean = false;

    // Campos del arqueo.
    opening_float: number = 0;
    cash_expenses: number = 0;
    notes: string = '';

    /** Moneda del país: manda qué billetes y monedas se cuentan. */
    currency: CurrencyProfile | null = null;
    /** Conteo por denominación. null cuando la moneda no tiene catálogo. */
    cash_count: CashCount | null = null;
    /**
     * Sin catálogo de denominaciones, el total contado se anota a mano.
     * Arranca en null: "no contado" no es lo mismo que "contado y dio cero".
     */
    manual_cash: number = null;
    /**
     * Cierre anterior guardado con un solo total, sin desglose. Se muestra
     * como referencia; para actualizarlo hay que volver a contar por denominación.
     */
    legacy_counted: number | null = null;

    /** Cierre o validación ya existentes, para no perder lo anotado antes. */
    existing_closing: any = null;
    /** Qué acción está guardando, para el texto del botón. */
    saving_status: ClosingStatus = null;
    existing_validation: any = null;
    /** Cajeros que vendieron en la caja (solo cuando el cierre abarca la caja completa). */
    cashiers: CashierTotals[] = [];

    constructor(
        private modalCtrl: ModalController,
        private eventClosing: EventClosingService,
        private SwalService: SwalService,
        public auth: AuthService,
        private connectivity: ConnectivityService,
    ) { }

    async ngOnInit() {
        this.loading = true;

        try {
            this.closingRequired = await this.eventClosing.isClosingRequired(this.event_key);
            this.currency = this.eventClosing.currencyProfile();
            this.cash_count = this.currency ? newCashCount(this.currency) : null;
            await this.loadBoxTotals();

            if (this.closingRequired) {
                const prev = await this.eventClosing.getClosing(this.event_key, this.box_key);
                if (prev) {
                    this.existing_closing = prev;
                    this.restoreCount(prev);
                    this.opening_float = Number(prev.opening_float || 0);
                    this.cash_expenses = Number(prev.cash_expenses || 0);
                    this.notes = prev.notes || '';
                }
            } else {
                this.existing_validation = await this.eventClosing.getValidation(this.event_key);
                if (this.existing_validation) {
                    this.notes = this.existing_validation.notes || '';
                }
            }
        } catch (e) {
            console.error('Error preparando el cierre:', e);
        }
        this.loading = false;
    }

    /**
     * Recupera lo contado en el cierre anterior. Con desglose guardado se
     * vuelven a llenar las denominaciones; si solo había un total (cierres
     * viejos, o moneda sin catálogo) se conserva ese total.
     */
    private restoreCount(prev: any) {
        const saved = prev.cash_count;
        const counted = prev.counts && prev.counts.cash ? prev.counts.cash.counted : null;
        const hasBreakdown = !!(saved && ((saved.bills && saved.bills.length) || (saved.coins && saved.coins.length)));

        if (this.currency && hasBreakdown) {
            this.cash_count = restoreCashCount(this.currency, saved);
            return;
        }
        if (counted === null || counted === undefined) return;
        if (this.currency) {
            this.legacy_counted = Number(counted);
        } else {
            this.manual_cash = Number(counted);
        }
    }

    /**
     * Recalcula los totales con TODAS las órdenes de la caja (y del usuario,
     * si se indicó), sin canceladas. Si la lectura falla se conservan los
     * totales que pasó la página y se avisa.
     */
    private async loadBoxTotals() {
        if (!this.event_key || !this.box_key) return;
        try {
            const orders = await this.eventClosing.getBoxOrders(this.event_key, this.box_key, this.account_key);
            const totals = this.eventClosing.boxTotalsFromOrders(orders, this.box_key, this.box_name);
            // Caja completa: se muestra quién compone el total (todos sus cajeros).
            this.cashiers = this.account_key ? [] : this.eventClosing.groupOrdersByCashier(orders, this.box_key);
            if (this.cashiers.length > 0) this.eventClosing.resolveCashierNames(this.cashiers).catch(() => { });
            this.total_orders = totals.total_orders;
            this.total_sales = totals.total_sales;
            this.total_cost_admin = totals.total_cost_admin;
            this.total_cash = totals.total_cash;
            this.total_card = totals.total_card;
            this.total_pos = totals.total_pos;
            this.orders_error = false;
        } catch (e) {
            console.error('No se pudieron leer las órdenes de la caja:', e);
            this.orders_error = true;
        }
    }

    // ── Estado del cierre ──────────────────────────────────────────────────

    /** La caja ya cerró en firme. */
    get is_final(): boolean {
        return this.eventClosing.isFinal(this.existing_closing);
    }

    /** Hay pre-cierre guardado pero la caja sigue abierta. */
    get is_pre_only(): boolean {
        return this.eventClosing.isPreOnly(this.existing_closing);
    }

    /** Ventas registradas después del último pre-cierre. */
    get sales_after_pre(): number {
        return this.eventClosing.salesAfterPreClose(this.existing_closing, this.total_sales);
    }

    // ── Cálculo en vivo ────────────────────────────────────────────────────

    /** Símbolo para los textos armados a mano; las plantillas usan el pipe currency. */
    get symbol(): string {
        return this.eventClosing.currencySymbol();
    }

    /** Totales del conteo por denominación (piezas y dinero), sin mutar nada. */
    get count_totals(): CashCountTotals {
        return cashCountTotals(this.cash_count);
    }

    lineSubtotal(line: CashCountLine): number {
        return lineSubtotal(line);
    }

    /**
     * Efectivo contado: la suma de billetes y monedas cuando hay catálogo;
     * si no, el total anotado a mano. null mientras no se cuente nada.
     */
    get counted_cash(): number | null {
        if (this.cash_count) return isCounted(this.cash_count) ? this.count_totals.total : null;
        return this.manual_cash;
    }

    get expected_cash(): number {
        return this.eventClosing.expectedCash(this.total_cash, this.opening_float, this.cash_expenses);
    }

    get difference(): number | null {
        return this.eventClosing.cashDifference(this.counted_cash, this.expected_cash);
    }

    get balanced(): boolean {
        return this.eventClosing.isBalanced(this.difference);
    }

    get counted(): boolean {
        return this.counted_cash !== null && this.counted_cash !== undefined && (this.counted_cash as any) !== '';
    }

    /**
     * Etiqueta del resultado. Deliberadamente neutra: "pendiente de revisión"
     * y no "faltante", que suena a acusación antes de saber qué pasó.
     */
    get difference_label(): string {
        const d = this.difference;
        if (d === null) return 'Sin arquear';
        if (Math.abs(d) < 0.01) return 'Coincide';
        return 'Pendiente de revisión';
    }

    // ── Acciones ───────────────────────────────────────────────────────────

    /** Cuadre provisional: guarda el conteo sin cerrar la caja. */
    preClose() {
        return this.saveWithStatus('pre');
    }

    /** Cierre definitivo de la caja para este evento. */
    finalClose() {
        return this.saveWithStatus('final');
    }

    private async saveWithStatus(status: ClosingStatus) {
        if (this.saving) return;

        if (!this.box_key) {
            this.SwalService.fire(
                'Sin zona seleccionada',
                'Elige la zona (tu caja) en el monitor antes de cerrar.',
                'error'
            );
            return;
        }

        if (!this.counted) {
            this.SwalService.fire(
                'Falta el arqueo',
                this.cash_count
                    ? 'Anota cuántos billetes y monedas hay de cada denominación antes de cerrar.'
                    : 'Anota cuánto efectivo contaste antes de cerrar el evento.',
                'warning'
            );
            return;
        }

        // Un descuadre no bloquea, pero no debe pasar inadvertido. En el
        // pre-cierre no se pregunta: es provisional y se puede rehacer.
        if (status === 'final' && !this.balanced) {
            const d = this.difference;
            const result: any = await this.SwalService.fire({
                title: 'El conteo no coincide',
                text: `Lo contado y lo esperado no coinciden por ${this.symbol}${Math.abs(d).toFixed(2)}. `
                    + 'Puedes cerrar y dejar la caja pendiente de revisión, o volver a contar.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Cerrar y dejar pendiente',
                cancelButtonText: 'Volver a contar',
            });
            if (!result || !result.value) return;
        }

        // Cerrar en firme es el paso que ya no se deshace desde la caja.
        if (status === 'final') {
            const confirm: any = await this.SwalService.fire({
                title: this.is_final ? '¿Reemplazar el cierre?' : '¿Cerrar la caja?',
                text: this.is_final
                    ? 'Esta caja ya está cerrada. Si continúas, se reemplaza el cierre anterior.'
                    : 'Después del cierre la caja queda marcada como cerrada para este evento.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: this.is_final ? 'Sí, reemplazar' : 'Sí, cerrar caja',
                cancelButtonText: 'Cancelar',
            });
            if (!confirm || !confirm.value) return;
        }

        this.saving = true;
        this.saving_status = status;
        try {
            await this.eventClosing.saveClosing({
                status: status,
                event_key: this.event_key,
                event_name: this.event_name,
                countryCode: this.auth.countryCode,
                box_key: this.box_key,
                box_name: this.box_name,
                total_orders: Number(this.total_orders || 0),
                total_sales: Number(this.total_sales || 0),
                total_cost_admin: Number(this.total_cost_admin || 0),
                counts: {
                    cash: { system: Number(this.total_cash || 0), counted: Number(this.counted_cash) },
                    card: { system: Number(this.total_card || 0), counted: null },
                    pos: { system: Number(this.total_pos || 0), counted: null },
                },
                currency: this.eventClosing.currencyInfo(),
                cash_count: this.cash_count ? cashCountForSave(this.cash_count) : null,
                opening_float: Number(this.opening_float || 0),
                cash_expenses: Number(this.cash_expenses || 0),
                difference: this.difference,
                notes: this.notes || '',
                // Foto de los totales al contar: permite ver después si la
                // caja siguió facturando tras el pre-cierre.
                pre_total_sales: Number(this.total_sales || 0),
                pre_total_cash: Number(this.total_cash || 0),
                pre_closed_at: this.existing_closing ? this.existing_closing.pre_closed_at : null,
            });

            const offlineNote = this.connectivity.isOffline
                ? ' Sin conexión: quedó guardado en este dispositivo y se enviará solo al volver la señal; no cierres la pestaña.'
                : '';
            this.SwalService.fire(
                '¡Listo!',
                (status === 'final'
                    ? 'La caja quedó cerrada.'
                    : 'Pre-cierre guardado. La caja sigue abierta y puedes rehacerlo cuando quieras.') + offlineNote,
                'success'
            );
            this.modalCtrl.dismiss({ closed: status === 'final', preClosed: status === 'pre' });
        } catch (e) {
            console.error('No se pudo guardar el cierre:', e);
            this.SwalService.fire('Error', 'No se pudo guardar. Intenta de nuevo.', 'error');
        }
        this.saving = false;
        this.saving_status = null;
    }

    /**
     * Anula el pre-cierre guardado: la caja vuelve a "sin cerrar" y el conteo
     * anotado se descarta. Solo mientras no haya cierre en firme.
     */
    async cancelPreClose() {
        if (this.saving || this.readonly || !this.is_pre_only) return;

        const confirm: any = await this.SwalService.fire({
            title: '¿Anular el pre-cierre?',
            text: 'Se descarta el conteo guardado y la caja vuelve a estar sin cerrar. Podrás hacer un pre-cierre nuevo cuando quieras.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#c0392b',
            cancelButtonColor: '#92949c',
            confirmButtonText: 'Sí, anular',
            cancelButtonText: 'Mantener pre-cierre',
        });
        if (!confirm || !confirm.value) return;

        this.saving = true;
        this.saving_status = 'canceled';
        try {
            await this.eventClosing.cancelClosing(this.event_key, this.box_key);
            const offlineNote = this.connectivity.isOffline
                ? ' Sin conexión: se enviará solo al volver la señal.'
                : '';
            this.SwalService.fire('Pre-cierre anulado', 'La caja vuelve a estar sin cerrar.' + offlineNote, 'success');
            this.modalCtrl.dismiss({ canceled: true });
        } catch (e) {
            console.error('No se pudo anular el pre-cierre:', e);
            this.SwalService.fire('Error', 'No se pudo anular el pre-cierre. Intenta de nuevo.', 'error');
        }
        this.saving = false;
        this.saving_status = null;
    }

    async confirmValidation() {
        if (this.saving) return;
        this.saving = true;
        try {
            await this.eventClosing.validateReport(this.event_key, this.event_name, this.notes);
            this.SwalService.fire('¡Listo!', 'El reporte quedó validado.', 'success');
            this.modalCtrl.dismiss({ validated: true });
        } catch (e) {
            console.error('No se pudo validar el reporte:', e);
            this.SwalService.fire('Error', 'No se pudo validar el reporte. Intenta de nuevo.', 'error');
        }
        this.saving = false;
    }

    dismiss() {
        this.modalCtrl.dismiss();
    }
}
